import React, { useState, useRef, useEffect, useCallback } from "react";
import { TableRow, TableCell, Typography, Box } from "@mui/material";
import FastEntryLineItemImage from "./FastEntryLineItemImage";
import InvoiceLineItemActions from "./InvoiceLineItemActions";
import FastEntryStyleSearchBar from "./FastEntryStyleSearchBar";
import FastEntryColorSearch from "./FastEntryColorSearch";
import FastEntrySizes from "./FastEntrySizes";
import FastEntryShortDescription from "./FastEntryShortDescription";
import useFocusFlow from "./hooks/useFocusFlow";
import { useFocusEvent, useHotkeyFocusEvent } from "./hooks/useFocusEvent";

const FastEntryInvoiceLineItem = ({ 
  item, 
  onRemove, 
  onDuplicate, 
  onUpdate, 
  onAddNewBelow, 
  isNewItem, 
  isDuplicatedItem,
  isHotKeyCreated,
  onFocus, 
  resetPoppers 
}) => {
  const [availableColors, setAvailableColors] = useState([]);
  const [selectedColor, setSelectedColor] = useState(item?.color || null);
  const [selectedStyle, setSelectedStyle] = useState(item?.style ? { 
    master_style: item.style,
    master_brand: item.brand,
    dgi_style_sku: item.dgi_style_sku,
    master_size_list: item.master_size_list
  } : null);
  
  // Create refs for the components
  const styleSearchRef = useRef(null);
  const colorSearchRef = useRef(null);
  const sizesRef = useRef(null);
  
  // Add a ref to prevent update loops
  const updateCountRef = useRef(0);
  
  // Instead of a ref, use state to store the methods
  const [styleSearchMethods, setStyleSearchMethods] = useState(null);
  const [colorSearchMethods, setColorSearchMethods] = useState(null);
  
  // Use our custom focus flow hook with method setters
  const { triggerStyleSelected, triggerColorSelected, resetFocusFlow } = useFocusFlow({
    refs: {
      styleRef: styleSearchRef,
      colorRef: colorSearchRef,
      sizesRef: sizesRef
    },
    setStyleSearchMethods,
    setColorSearchMethods,
    state: {
      isNewItem: isNewItem && !isDuplicatedItem,
      isHotKeyCreated,
      availableColors,
      selectedStyle,
      selectedColor,
      resetPoppers
    }
  });
  
  // Add event listener for keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for Shift+Enter
      if (e.key === 'Enter' && e.shiftKey) {
        e.preventDefault(); // Prevent default behavior
        
        // Reset the focus flow to prevent auto-focusing to color
        resetFocusFlow();
        
        // Call the function to add a new item below this one
        onAddNewBelow(item);
      }
    };
    
    // Add the event listener
    document.addEventListener('keydown', handleKeyDown);
    
    // Clean up the event listener when component unmounts
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [item, onAddNewBelow, resetFocusFlow]);
  
  // Add these event handlers to track focus
  const handleFocusIn = () => {
    if (onFocus) {
      onFocus();
    }
  };

  // Add event listeners for focus
  useEffect(() => {
    const rowElement = document.getElementById(`line-item-${item.id}`);
    if (rowElement) {
      rowElement.addEventListener('focusin', handleFocusIn);
      
      return () => {
        rowElement.removeEventListener('focusin', handleFocusIn);
      };
    }
  }, [item.id, onFocus]);

  const handleDuplicate = () => {
    if (onDuplicate) {
      onDuplicate(item);
    }
  };

  const handleStyleSelect = useCallback((style) => {
    // Make sure we have all the required fields
    if (!style) return;
    
    // Check if this is a new style selection or just editing an existing style
    const isNewSelection = !selectedStyle || selectedStyle.master_style !== style.master_style;
    
    // Update the selected style state
    setSelectedStyle(style);
    
    // Update the item with the selected style data
    const updatedItem = {
      ...item,
      style: style.master_style,
      brand: style.master_brand,
      dgi_style_sku: style.dgi_style_sku, // Make sure this is set
      description: style.master_short_description || '',
      master_size_list: style.master_size_list
    };
    
    // If this is a new style selection, reset the sizes
    if (isNewSelection) {
      updatedItem.sizes = {};
      updatedItem.totalQuantity = 0;
    }
    
    // Update the parent component
    onUpdate(updatedItem);
    
    // Only trigger the focus flow if this is a new style selection and not a duplicated item
    if (isNewSelection && !isDuplicatedItem) {
      triggerStyleSelected();
    }
  }, [item, onUpdate, triggerStyleSelected, isDuplicatedItem, selectedStyle]);

  // Add a new handler for when the style is being edited
  const handleStyleEdit = useCallback(() => {
    // Clear the selected style
    setSelectedStyle(null);
    
    // Clear the selected color
    setSelectedColor(null);
    
    // Clear available colors
    setAvailableColors([]);
    
    // Update the item with cleared style data
    const updatedItem = {
      ...item,
      style: "",
      brand: "",
      dgi_style_sku: "",
      description: "",
      color: "",
      master_size_list: []
    };
    
    // Update the parent component
    onUpdate(updatedItem);
  }, [item, onUpdate]);

  const handleColorSelect = (color) => {
    updateCountRef.current++;
    
    setSelectedColor(color);
    
    // Only trigger the focus flow if this is not a duplicated item
    if (!isDuplicatedItem) {
      triggerColorSelected();
    }
    
    if (onUpdate) {
      onUpdate({
        ...item,
        color: color
      });
    }
  };

  // Create a merged item with the selected style's master_size_list
  const mergedItem = {
    ...item,
    master_size_list: selectedStyle?.master_size_list || item.master_size_list
  };
  
  const handleSizesUpdate = (updatedItem) => {
    if (onUpdate) {
      // Pass along the totalQuantity from the sizes component
      onUpdate({
        ...item,
        ...updatedItem,
        totalQuantity: updatedItem.totalQuantity || 0
      });
    }
  };

  // Create a callback to focus the style search input
  const focusStyleSearch = useCallback(() => {
    // Find the input element directly
    const inputElement = document.querySelector(`#style-search-${item.id} input`);
    if (inputElement) {
      inputElement.focus();
    }
  }, [item.id]);
  
  // Listen for focus events for this specific item
  useFocusEvent(item.id, focusStyleSearch);
  
  // Use the new hook for hotkey-created items
  useHotkeyFocusEvent(item.id, isHotKeyCreated);

  // Change the highlightId creation to always generate a unique ID
  const highlightId = item.id; // Use the item's unique ID instead of style+color
  
  // Modify the metadata to include the current selection state
  const highlightMetadata = {
    dgi_style_sku: selectedStyle?.dgi_style_sku || item.dgi_style_sku,
    master_color: selectedColor || item.color,
    style: selectedStyle?.master_style || item.style,
    description: selectedStyle?.master_short_description || item.description,
    sizes: Object.keys(item.sizes || {}),
    invoiceNumber: item.invoiceNumber,
    // Include item ID to ensure uniqueness
    itemId: item.id
  };

  return (
    <TableRow
      id={`line-item-${item.id}`}
      sx={{
        "&:not(:first-of-type)": {
          borderTop: "1px dashed rgba(0, 0, 0, 0.12)",
        },
        marginBottom: "16px",
        "& > td": {
          paddingBottom: "16px"
        }
      }}
    >
      <TableCell
        sx={{
          verticalAlign: "top",
          width: "100px",
          padding: "8px",
          paddingBottom: "16px",
          borderBottom: "none",
        }}
      >
        <FastEntryLineItemImage 
          src={item.imgUrl} 
          productUrl={item.productUrl} 
          dgiStyleSku={selectedStyle?.dgi_style_sku || item.dgi_style_sku}
          color={selectedColor || item.color}
        />
      </TableCell>
      <TableCell
        sx={{
          verticalAlign: "top",
          width: "90%",
          padding: "8px",
          paddingBottom: "16px",
          borderBottom: "none",
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {/* First row: Style and Color search */}
          <Box sx={{ display: 'flex', gap: '16px', marginRight: '12px' }}>
            {/* Style Search */}
            <Box sx={{ flexBasis: '55%', flexGrow: 0, flexShrink: 0 }}>
              <FastEntryStyleSearchBar
                id={`style-search-${item.id}`}
                ref={styleSearchRef}
                onStyleSelect={handleStyleSelect}
                onStyleEdit={handleStyleEdit}
                setAvailableColors={setAvailableColors}
                item={item}
                selectedStyle={selectedStyle}
                setSelectedColor={setSelectedColor}
                initialSelectedColor={item.color}
                onColorSelect={handleColorSelect}
                defaultValue={item.style ? `${item.style} - ${item.brand}` : ""}
                onUpdate={(styleData) => {
                  if (onUpdate) {
                    onUpdate({
                      ...item,
                      ...styleData
                    });
                  }
                }}
                onMount={setStyleSearchMethods}
              />
            </Box>
            
            {/* Color Search */}
            <Box sx={{ flexBasis: '45%', flexGrow: 0, flexShrink: 0 }}>
              <FastEntryColorSearch
                ref={colorSearchRef}
                item={item}
                onColorSelect={handleColorSelect}
                availableColors={availableColors}
                selectedStyle={selectedStyle}
                initialSelectedColor={selectedColor}
                disabled={!selectedStyle}
                onMount={setColorSearchMethods}
              />
            </Box>
          </Box>
          
          {/* Short Description */}
          <FastEntryShortDescription 
            master_short_description={selectedStyle?.master_short_description || item.description}
            selectedStyle={selectedStyle}
            selectedColor={selectedColor || item.color}
          />
          
          {/* Second row: Sizes */}
          <Box sx={{ paddingLeft: '8px' }}>
            <FastEntrySizes 
              ref={sizesRef}
              item={mergedItem}
              onUpdate={handleSizesUpdate}
            />
          </Box>
        </Box>
      </TableCell>
      <TableCell
        sx={{
          verticalAlign: "top",
          width: "40px",
          padding: "0",
          paddingBottom: "1.5rem",
          borderBottom: "none",
          position: "relative",
          height: "100%"
        }}
      >
        <InvoiceLineItemActions 
          onCopy={handleDuplicate}
          onDelete={onRemove}
          itemId={highlightId}
          metadata={highlightMetadata}
        />
      </TableCell>
    </TableRow>
  );
};

export default FastEntryInvoiceLineItem;